import React, { useState, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../core/firebase/firebase';
import { useAuth } from '../../core/firebase/useAuth';
import { useAuthModal } from '../../core/AuthModalContext';
import { useLang } from '../../core/i18n';
import { IconImage, IconX, IconBolt, IconLink } from '../../components/Icons';

export default function CreerPage() {
  const { user }           = useAuth();
  const { showAuthModal }  = useAuthModal();
  const { t }              = useLang();

  const [mode, setMode] = useState('product'); // 'product' | 'video'

  // — Produit —
  const [title,      setTitle]      = useState('');
  const [price,      setPrice]      = useState('');
  const [category,   setCategory]   = useState('');
  const [desc,       setDesc]       = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [imgFile,    setImgFile]    = useState(null);
  const [imgPrev,    setImgPrev]    = useState(null);

  // — Vidéo —
  const [videoUrl,      setVideoUrl]      = useState('');
  const [videoCaption,  setVideoCaption]  = useState('');
  const [videoPrice,    setVideoPrice]    = useState('');
  const [videoCategory, setVideoCategory] = useState('');

  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');
  const fileRef = useRef(null);

  const CATEGORIES = [
    { label: t.vetements,    value: 'clothing'    },
    { label: t.chaussures,   value: 'shoes'       },
    { label: t.electronique, value: 'electronics' },
    { label: t.jeuxVideo,    value: 'gaming'      },
    { label: t.laptops,      value: 'laptops'     },
    { label: t.cameras,      value: 'cameras'     },
    { label: t.maison,       value: 'home'        },
    { label: t.autre,        value: 'other'       },
  ];

  const pickImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    setImgPrev(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImgFile(null); setImgPrev(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const resetAll = () => {
    setTitle(''); setPrice(''); setCategory(''); setDesc(''); setProductUrl('');
    clearImage();
    setVideoUrl(''); setVideoCaption(''); setVideoPrice(''); setVideoCategory('');
    setError('');
  };

  // ── Publier produit ──────────────────────────────────────────────────────
  const handleSubmitProduct = async () => {
    if (!user)         { showAuthModal(); return; }
    if (!title.trim()) { setError(t.titleRequired); return; }
    if (!category)     { setError(t.chooseRequired); return; }

    setError(''); setLoading(true);
    try {
      let imageUrl = '';
      if (imgFile) {
        const path = `user_products/${user.uid}/${Date.now()}_${imgFile.name}`;
        const snap  = await uploadBytes(storageRef(storage, path), imgFile);
        imageUrl    = await getDownloadURL(snap.ref);
      }
      await addDoc(collection(db, 'user_products'), {
        title      : title.trim(),
        price      : price ? parseFloat(price) : null,
        priceText  : price ? price.toString() : '',
        category,
        description: desc.trim(),
        link       : productUrl.trim(),
        imageUrl,
        uid        : user.uid,
        userId     : user.uid,
        username   : user.displayName || '',
        displayName: user.displayName || '',
        photoURL   : user.photoURL || '',
        createdAt  : serverTimestamp(),
        likes      : 0,
        likedBy    : [],
        viewCount  : 0,
        available  : true,
        isCreator  : true,
      });
      setDone(true);
      resetAll();
    } catch (e) {
      if (e.code === 'storage/unauthorized')            setError('Accès Storage refusé. Vérifiez les règles Firebase Storage.');
      else if (e.code?.includes('permission-denied'))   setError('Accès Firestore refusé. Vérifiez les règles Firestore.');
      else                                              setError(e.message || t.error);
    } finally { setLoading(false); }
  };

  // ── Publier vidéo ────────────────────────────────────────────────────────
  const handleSubmitVideo = async () => {
    if (!user)             { showAuthModal(); return; }
    if (!videoUrl.trim())  { setError('Ajoute le lien de ta vidéo.'); return; }
    if (!videoCategory)    { setError(t.chooseRequired); return; }

    setError(''); setLoading(true);
    try {
      await addDoc(collection(db, 'posts'), {
        type        : 'video',
        videoUrl    : videoUrl.trim(),
        caption     : videoCaption.trim(),
        price       : videoPrice ? parseFloat(videoPrice) : null,
        priceText   : videoPrice ? videoPrice.toString() : '',
        category    : videoCategory,
        uid         : user.uid,
        userId      : user.uid,
        username    : user.displayName || '',
        displayName : user.displayName || '',
        photoURL    : user.photoURL || '',
        createdAt   : serverTimestamp(),
        likes       : 0,
        likedBy     : [],
        viewCount   : 0,
        available   : true,
      });
      setDone(true);
      resetAll();
    } catch (e) {
      if (e.code?.includes('permission-denied')) setError('Accès Firestore refusé. Vérifiez les règles Firestore.');
      else                                       setError(e.message || t.error);
    } finally { setLoading(false); }
  };

  // ── Guards ───────────────────────────────────────────────────────────────
  if (!user) return (
    <div style={st.centered}>
      <div style={st.lockBox}><IconBolt size={32} color="#4361ee" /></div>
      <h2 style={st.lockTitle}>{t.loginToPublish}</h2>
      <p style={st.lockSub}>{t.loginSub}</p>
      <button style={st.btnPrimary} onClick={showAuthModal}>{t.login}</button>
    </div>
  );

  if (done) return (
    <div style={st.centered}>
      <div style={{ ...st.lockBox, background: '#e8f8f1' }}>
        <IconBolt size={32} color="#27ae60" />
      </div>
      <h2 style={{ ...st.lockTitle, color: '#27ae60' }}>{t.published}</h2>
      <p style={st.lockSub}>{t.publishedSub}</p>
      <button style={st.btnPrimary} onClick={() => setDone(false)}>{t.publishAnother}</button>
    </div>
  );

  // ── UI ───────────────────────────────────────────────────────────────────
  return (
    <div style={st.page}>
      <h2 style={st.pageTitle}>{t.newProduct}</h2>

      {/* Sélecteur de mode */}
      <div style={st.modeRow}>
        <button
          style={{ ...st.modeBtn, ...(mode === 'product' ? st.modeBtnOn : {}) }}
          onClick={() => { setMode('product'); setError(''); }}
        >
          🛍️ Produit
        </button>
        <button
          style={{ ...st.modeBtn, ...(mode === 'video' ? st.modeBtnOn : {}) }}
          onClick={() => { setMode('video'); setError(''); }}
        >
          🎬 Vidéo
        </button>
      </div>

      {/* ── FORMULAIRE PRODUIT ── */}
      {mode === 'product' && (
        <>
          <div style={st.section}>
            {imgPrev ? (
              <div style={st.previewWrap}>
                <img src={imgPrev} alt="preview" style={st.preview} />
                <button style={st.removeImg} onClick={clearImage}><IconX size={16} color="#fff" /></button>
              </div>
            ) : (
              <button style={st.imagePicker} onClick={() => fileRef.current?.click()}>
                <IconImage size={32} color="#4361ee" />
                <span style={st.imagePickerText}>{t.addPhoto}</span>
                <span style={st.imagePickerSub}>{t.photoSub}</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickImage} />
          </div>

          <Field label={t.titleLabel}>
            <input style={st.input} placeholder={t.titlePlaceholder} value={title} onChange={e => setTitle(e.target.value)} maxLength={120} />
          </Field>

          <Field label={t.priceLabel}>
            <input style={st.input} placeholder="0.00" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
          </Field>

          <Field label={t.categoryLabel}>
            <select style={{ ...st.input, color: category ? '#1a1a1a' : '#8888aa', appearance: 'none' }} value={category} onChange={e => setCategory(e.target.value)}>
              <option value="" disabled>{t.chooseCategory}</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>

          <Field label="🔗 Lien du produit">
            <div style={st.linkWrap}>
              <IconLink size={16} color="#8888aa" />
              <input style={{ ...st.input, border: 'none', padding: '0', flex: 1, outline: 'none' }} placeholder="https://..." value={productUrl} onChange={e => setProductUrl(e.target.value)} type="url" />
            </div>
          </Field>

          <Field label={t.descLabel}>
            <textarea style={{ ...st.input, height: 96, resize: 'none' }} placeholder={t.descPlaceholder} value={desc} onChange={e => setDesc(e.target.value)} maxLength={600} />
          </Field>

          {error && <p style={st.error}>{error}</p>}
          <button style={{ ...st.btnSubmit, opacity: loading ? 0.7 : 1 }} onClick={handleSubmitProduct} disabled={loading}>
            {loading ? t.publishing : t.publish}
          </button>
        </>
      )}

      {/* ── FORMULAIRE VIDÉO ── */}
      {mode === 'video' && (
        <>
          {/* Lien vidéo */}
          <Field label="🔗 Lien de la vidéo">
            <div style={st.linkWrap}>
              <span style={{ fontSize: 18 }}>🎬</span>
              <input
                style={{ ...st.input, border: 'none', padding: '0', flex: 1, outline: 'none' }}
                placeholder="https://youtube.com/... ou TikTok, Instagram..."
                value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)}
                type="url"
              />
            </div>
          </Field>

          {/* Aperçu si YouTube */}
          {videoUrl && getYoutubeId(videoUrl) && (
            <div style={st.videoPreview}>
              <iframe
                style={st.videoIframe}
                src={`https://www.youtube.com/embed/${getYoutubeId(videoUrl)}`}
                title="Aperçu"
                allowFullScreen
              />
            </div>
          )}

          {/* Caption courte */}
          <Field label="✏️ Description courte">
            <textarea
              style={{ ...st.input, height: 72, resize: 'none' }}
              placeholder="Décris ta vidéo en quelques mots..."
              value={videoCaption}
              onChange={e => setVideoCaption(e.target.value)}
              maxLength={280}
            />
            <p style={st.charCount}>{videoCaption.length}/280</p>
          </Field>

          {/* Prix */}
          <Field label={t.priceLabel}>
            <input style={st.input} placeholder="0.00 (optionnel)" type="number" min="0" step="0.01" value={videoPrice} onChange={e => setVideoPrice(e.target.value)} />
          </Field>

          {/* Catégorie */}
          <Field label={t.categoryLabel}>
            <select style={{ ...st.input, color: videoCategory ? '#1a1a1a' : '#8888aa', appearance: 'none' }} value={videoCategory} onChange={e => setVideoCategory(e.target.value)}>
              <option value="" disabled>{t.chooseCategory}</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>

          {error && <p style={st.error}>{error}</p>}
          <button style={{ ...st.btnSubmit, opacity: loading ? 0.7 : 1 }} onClick={handleSubmitVideo} disabled={loading}>
            {loading ? t.publishing : '🎬 Publier la vidéo'}
          </button>
        </>
      )}
    </div>
  );
}

function getYoutubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) return u.searchParams.get('v');
    if (u.hostname === 'youtu.be') return u.pathname.slice(1);
  } catch {}
  return null;
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const st = {
  page           : { background: '#f7f8fa', minHeight: '100vh', padding: '20px 16px 100px', maxWidth: 520, margin: '0 auto' },
  pageTitle      : { color: '#1a1a1a', fontSize: 22, fontWeight: 700, marginBottom: 20 },
  modeRow        : { display: 'flex', gap: 10, marginBottom: 24 },
  modeBtn        : { flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid #eaeaea', background: '#fff', fontSize: 15, fontWeight: 600, color: '#888', cursor: 'pointer', transition: 'all .2s' },
  modeBtnOn      : { background: '#4361ee', borderColor: '#4361ee', color: '#fff' },
  section        : { marginBottom: 16 },
  input          : { width: '100%', background: '#fff', border: '1.5px solid #eaeaea', borderRadius: 12, padding: '12px 14px', fontSize: 15, color: '#1a1a1a', outline: 'none', boxSizing: 'border-box' },
  linkWrap       : { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1.5px solid #eaeaea', borderRadius: 12, padding: '12px 14px' },
  imagePicker    : { width: '100%', height: 140, border: '2px dashed #d0d4ff', borderRadius: 14, background: '#f8f9ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' },
  imagePickerText: { fontSize: 14, fontWeight: 600, color: '#4361ee' },
  imagePickerSub : { fontSize: 12, color: '#aaa' },
  previewWrap    : { position: 'relative', width: '100%', height: 180, borderRadius: 14, overflow: 'hidden' },
  preview        : { width: '100%', height: '100%', objectFit: 'cover' },
  removeImg      : { position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  videoPreview   : { width: '100%', borderRadius: 14, overflow: 'hidden', marginBottom: 14, background: '#000', aspectRatio: '16/9' },
  videoIframe    : { width: '100%', height: '100%', border: 'none' },
  charCount      : { fontSize: 11, color: '#bbb', textAlign: 'right', margin: '4px 0 0' },
  error          : { color: '#e74c3c', fontSize: 13, marginBottom: 10, textAlign: 'center' },
  btnSubmit      : { width: '100%', background: '#4361ee', color: '#fff', border: 'none', borderRadius: 14, height: 52, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 8 },
  centered       : { background: '#f7f8fa', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px 80px' },
  lockBox        : { background: '#eef0ff', borderRadius: 20, width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  lockTitle      : { color: '#1a1a1a', fontSize: 22, fontWeight: 700, marginBottom: 8 },
  lockSub        : { color: '#888', fontSize: 14, marginBottom: 32, textAlign: 'center', maxWidth: 280 },
  btnPrimary     : { background: '#4361ee', color: '#fff', border: 'none', borderRadius: 14, height: 52, width: '100%', maxWidth: 320, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
};
